import React, { useState, useEffect } from 'react';
import { Button, Modal, Form, Input, SelectPicker, Message, Loader } from 'rsuite';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faSave, faTimes } from '@fortawesome/free-solid-svg-icons';
import { PanelService } from '../../../../network/panel/network';
import { IOffersSection } from '@bprotsyk/aso-core';
import './_sections-manager.scss';
import { createLogger } from '../../../../utils/logger';

const logger = createLogger('sections-manager');

interface SectionsManagerProps {
  onSectionsChange?: (sections: IOffersSection[]) => void;
}

interface TranslationData {
  none: string;
  [key: string]: string;
}

interface TranslationCard {
  countryCode: string;
  countryName: string;
  title: string;
  button: string;
}

interface TranslationCardProps {
  card: TranslationCard;
  onUpdate: (card: TranslationCard) => void;
  onRemove: () => void;
}

interface DefaultTranslationCardProps {
  title: string;
  button: string;
  onUpdate: (title: string, button: string) => void;
}

const TranslationCard: React.FC<TranslationCardProps> = ({ card, onUpdate, onRemove }) => {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingButton, setIsEditingButton] = useState(false);
  const [editingTitle, setEditingTitle] = useState(card.title);
  const [editingButton, setEditingButton] = useState(card.button);

  const handleTitleClick = () => {
    setIsEditingTitle(true);
    setEditingTitle(card.title);
  };

  const handleButtonClick = () => {
    setIsEditingButton(true);
    setEditingButton(card.button);
  };

  const handleTitleSave = () => {
    setIsEditingTitle(false);
    onUpdate({ ...card, title: editingTitle });
  };

  const handleButtonSave = () => {
    setIsEditingButton(false);
    onUpdate({ ...card, button: editingButton });
  };

  const handleTitleCancel = () => {
    setIsEditingTitle(false);
    setEditingTitle(card.title);
  };

  const handleButtonCancel = () => {
    setIsEditingButton(false);
    setEditingButton(card.button);
  };

  return (
    <div className="translation-item-modal">
      <span className="flag-container">
        {card.countryCode === 'none' ? '🌍' : getCountryFlag(card.countryCode)}
      </span>
      <div className="translation-content">
        <div className="translation-title">
          {isEditingTitle ? (
            <div className="inline-edit">
              <Input
                value={editingTitle}
                onChange={setEditingTitle}
                onPressEnter={handleTitleSave}
                onBlur={handleTitleSave}
                autoFocus
                size="sm"
              />
              <Button size="xs" onClick={handleTitleSave}>
                <FontAwesomeIcon icon={faSave} />
              </Button>
              <Button size="xs" onClick={handleTitleCancel}>
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            </div>
          ) : (
            <span onClick={handleTitleClick} className="editable-text">
              {card.title || '-'}
            </span>
          )}
        </div>
        <div className="translation-button">
          {isEditingButton ? (
            <div className="inline-edit">
              <Input
                value={editingButton}
                onChange={setEditingButton}
                onPressEnter={handleButtonSave}
                onBlur={handleButtonSave}
                autoFocus
                size="sm"
              />
              <Button size="xs" onClick={handleButtonSave}>
                <FontAwesomeIcon icon={faSave} />
              </Button>
              <Button size="xs" onClick={handleButtonCancel}>
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            </div>
          ) : (
            <span onClick={handleButtonClick} className="editable-text">
              <strong>🔘 {card.button || '-'}</strong>
            </span>
          )}
        </div>
      </div>
      <div className="close-button" onClick={onRemove}>
        <FontAwesomeIcon icon={faTimes} />
      </div>
    </div>
  );
};

const DefaultTranslationCard: React.FC<DefaultTranslationCardProps> = ({ title, button, onUpdate }) => {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingButton, setIsEditingButton] = useState(false);
  const [editingTitle, setEditingTitle] = useState(title);
  const [editingButton, setEditingButton] = useState(button);

  const handleTitleClick = () => {
    setIsEditingTitle(true);
    setEditingTitle(title);
  };

  const handleButtonClick = () => {
    setIsEditingButton(true);
    setEditingButton(button);
  };

  const handleTitleSave = () => {
    setIsEditingTitle(false);
    onUpdate(editingTitle, editingButton);
  };

  const handleButtonSave = () => {
    setIsEditingButton(false);
    onUpdate(title, editingButton);
  };

  const handleTitleCancel = () => {
    setIsEditingTitle(false);
    setEditingTitle(title);
  };

  const handleButtonCancel = () => {
    setIsEditingButton(false);
    setEditingButton(button);
  };

  return (
    <div className="translation-item-modal default-card">
      <span className="flag-container">
        🌍
      </span>
      <div className="translation-content">
        <div className="translation-title">
          {isEditingTitle ? (
            <div className="inline-edit">
              <Input
                value={editingTitle}
                onChange={setEditingTitle}
                onPressEnter={handleTitleSave}
                onBlur={handleTitleSave}
                autoFocus
                size="sm"
              />
              <Button size="xs" onClick={handleTitleSave}>
                <FontAwesomeIcon icon={faSave} />
              </Button>
              <Button size="xs" onClick={handleTitleCancel}>
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            </div>
          ) : (
            <span onClick={handleTitleClick} className="editable-text">
              {title || '-'}
            </span>
          )}
        </div>
        <div className="translation-button">
          {isEditingButton ? (
            <div className="inline-edit">
              <Input
                value={editingButton}
                onChange={setEditingButton}
                onPressEnter={handleButtonSave}
                onBlur={handleButtonSave}
                autoFocus
                size="sm"
              />
              <Button size="xs" onClick={handleButtonSave}>
                <FontAwesomeIcon icon={faSave} />
              </Button>
              <Button size="xs" onClick={handleButtonCancel}>
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            </div>
          ) : (
            <span onClick={handleButtonClick} className="editable-text">
              <strong>🔘 {button || '-'}</strong>
            </span>
          )}
        </div>
      </div>
      <div className="close-button">
        <FontAwesomeIcon icon={faTimes} />
      </div>
    </div>
  );
};

const getCountryFlag = (countryCode: string): string => {
  const flagEmojis: { [key: string]: string } = {
    'ua': '🇺🇦',
    'ru': '🇷🇺',
    'en': '🇺🇸',
    'de': '🇩🇪',
    'fr': '🇫🇷',
    'es': '🇪🇸',
    'it': '🇮🇹',
    'pl': '🇵🇱',
    'pt': '🇵🇹',
    'br': '🇧🇷',
    'ar': '🇦🇷',
    'mx': '🇲🇽',
    'ca': '🇨🇦',
    'au': '🇦🇺',
    'in': '🇮🇳',
    'jp': '🇯🇵',
    'kr': '🇰🇷',
    'cn': '🇨🇳',
    'th': '🇹🇭',
    'vn': '🇻🇳',
    'id': '🇮🇩',
    'my': '🇲🇾',
    'sg': '🇸🇬',
    'ph': '🇵🇭',
    'tr': '🇹🇷',
    'gr': '🇬🇷',
    'ro': '🇷🇴',
    'bg': '🇧🇬',
    'hr': '🇭🇷',
    'rs': '🇷🇸',
    'ba': '🇧🇦',
    'me': '🇲🇪',
    'mk': '🇲🇰',
    'al': '🇦🇱',
    'si': '🇸🇮',
    'hu': '🇭🇺',
    'cz': '🇨🇿',
    'sk': '🇸🇰',
    'at': '🇦🇹',
    'ch': '🇨🇭',
    'be': '🇧🇪',
    'nl': '🇳🇱',
    'dk': '🇩🇰',
    'no': '🇳🇴',
    'se': '🇸🇪',
    'fi': '🇫🇮',
    'is': '🇮🇸',
    'ie': '🇮🇪',
    'gb': '🇬🇧',
    'cy': '🇨🇾',
    'mt': '🇲🇹',
    'ee': '🇪🇪',
    'lv': '🇱🇻',
    'lt': '🇱🇹',
    'none': '🌍'
  };
  
  return flagEmojis[countryCode] || '🌍';
};

const SectionsManager: React.FC<SectionsManagerProps> = ({ onSectionsChange }) => {
  const [sections, setSections] = useState<IOffersSection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<IOffersSection | null>(null);
  const [formData, setFormData] = useState({
    id: 0,
    title: { none: '' } as TranslationData,
    button: { none: '' } as TranslationData
  });
  const [translationCards, setTranslationCards] = useState<TranslationCard[]>([]);

  const defaultSections = [
    { id: 0, name: 'GAMBLING_MAIN' },
    { id: 1, name: 'BETTING_MAIN' },
    { id: 2, name: 'GAMBLING_PLAYED' },
    { id: 3, name: 'BETTING_PLAYED' }
  ];

  const availableCountries = [
    { code: 'ua', name: 'Україна' },
    { code: 'en', name: 'English' },
    { code: 'ru', name: 'Русский' }
  ];

  const countryNames: { [key: string]: string } = {
    'none': 'За замовчуванням',
    'ua': 'Україна',
    'en': 'English',
    'ru': 'Русский'
  };

  const countryPickerData = availableCountries.map(country => ({
    label: (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span>{getCountryFlag(country.code)}</span>
        <span>{country.name}</span>
      </div>
    ),
    value: country.code
  })).filter(country => 
    !translationCards.some(card => card.countryCode === country.value)
  );

  useEffect(() => {
    fetchSections();
  }, []);

  const fetchSections = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const sectionsData = await PanelService.getSections();
      
      // Ensure sections have proper structure
      const normalizedSections = sectionsData.map((section: any) => ({
        id: section.id,
        title: section.title || { none: '' },
        button: section.button || { none: '' }
      }));
      
      setSections(normalizedSections);
      onSectionsChange?.(normalizedSections);
    } catch (error: any) {
      logger.error('Error fetching sections', error);
      const errorMessage = error.response?.data || error.message || 'Failed to load sections';
      setError(`Error: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const getAvailableTranslations = (section: IOffersSection) => {
    const translations: TranslationCard[] = [];
    
    // Always include 'none' as it's required
    translations.push({
      countryCode: 'none',
      countryName: countryNames['none'],
      title: section.title?.none || '',
      button: section.button?.none || ''
    });

    // Add other translations that exist
    if (section.title) {
      Object.entries(section.title).forEach(([code, value]) => {
        if (code !== 'none' && value && countryNames[code]) {
          translations.push({
            countryCode: code,
            countryName: countryNames[code],
            title: value as string,
            button: section.button?.[code] || ''
          });
        }
      });
    }

    return translations;
  };

  const handleCreateSection = () => {
    setFormData({
      id: 0,
      title: { none: '' } as TranslationData,
      button: { none: '' } as TranslationData
    });
    setTranslationCards([
      { countryCode: 'none', countryName: 'За замовчуванням', title: '', button: '' }
    ]);
    setIsCreateModalOpen(true);
  };

  const handleEditSection = (section: IOffersSection) => {
    setEditingSection(section);
    setFormData({
      id: section.id,
      title: section.title || { none: '' },
      button: section.button || { none: '' }
    });
    setTranslationCards(getAvailableTranslations(section));
    setIsEditModalOpen(true);
  };

  const handleDeleteSection = async (sectionId: number) => {
    try {
      await PanelService.deleteSection(sectionId);
      await fetchSections();
    } catch (error: any) {
      logger.error('Error deleting section', error);
      const errorMessage = error.response?.data || error.message || 'Failed to delete section';
      logger.error(errorMessage);
    }
  };

  const handleSaveSection = async () => {
    try {
      const title: { [key: string]: string } = {};
      const button: { [key: string]: string } = {};

      translationCards.forEach(card => {
        title[card.countryCode] = card.title;
        button[card.countryCode] = card.button;
      });

      // Гарантируем, что ключ 'none' есть всегда
      if (!('none' in title)) title['none'] = formData.title.none || '';
      if (!('none' in button)) button['none'] = formData.button.none || '';

      const sectionData: IOffersSection = {
        id: formData.id,
        title: title as { [key: string]: string; none: string },
        button: button as { [key: string]: string; none: string }
      };

      if (editingSection) {
        await PanelService.updateSection(editingSection.id, sectionData);
      } else {
        await PanelService.createSection(sectionData);
      }

      setIsCreateModalOpen(false);
      setIsEditModalOpen(false);
      setEditingSection(null);
      await fetchSections();
    } catch (error: any) {
      logger.error('Error saving section', error);
      const errorMessage = error.response?.data || error.message || 'Failed to save section';
      logger.error(errorMessage);
    }
  };

  const addTranslation = () => {
    // This function is now handled by the country picker in the modal
  };

  const handleCountrySelect = (countryCode: string) => {
    if (countryCode) {
      const newCard: TranslationCard = {
        countryCode,
        countryName: countryNames[countryCode],
        title: '',
        button: ''
      };
      setTranslationCards([...translationCards, newCard]);
    }
  };

  const removeTranslation = (countryCode: string) => {
    if (countryCode !== 'none') {
      setTranslationCards(translationCards.filter(card => card.countryCode !== countryCode));
    }
  };

  const isDefaultSection = (sectionId: number) => {
    return defaultSections.some(section => section.id === sectionId);
  };

  if (isLoading) {
    return (
      <div className="sections-manager">
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '200px'
        }}>
          <div>Завантаження секцій...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sections-manager">
        <div className="error-message">
          <p>{error}</p>
          <Button onClick={fetchSections}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="sections-manager">
      <div className="sections-header">
        <h2>Управління секціями</h2>
        <Button 
          appearance="primary"
          onClick={handleCreateSection}
        >
          <FontAwesomeIcon icon={faPlus} style={{ marginRight: '8px' }} />
          Додати секцію
        </Button>
      </div>

      {sections.length === 0 ? (
        <div className="empty-sections">
          <p>Секції не знайдено.</p>
        </div>
      ) : (
        <div className="sections-list">
          {sections.map((section) => (
            <div key={section.id} className="section-item">
              <div className="section-info">
                <div className="section-header">
                  <h3>{section.title.none || section.title.NONE || 'Untitled'}</h3>
                  <span className="section-id">#{section.id}</span>
                  {isDefaultSection(section.id) && (
                    <span className="default-badge">Default</span>
                  )}
                </div>
                <div className="section-translations">
                  <div className="translation-group">
                    <div className="translations">
                      {/* Дефолт перевод */}
                      <span className="translation-item">
                        <span className="flag-container">🌍</span>
                        <div className="translation-content">
                          <div className="translation-title">{section.title?.none || '-'}</div>
                          <div className="translation-button"><strong>🔘 {section.button?.none || '-'}</strong></div>
                        </div>
                      </span>
                      
                      {/* Другие переводы */}
                      {section.title && Object.entries(section.title).map(([code, value]) => {
                        if (code === 'none') return null;
                        if (!value) return null;
                        return (
                          <span key={code} className="translation-item">
                            <span className="flag-container">
                              {getCountryFlag(code)}
                            </span>
                            <div className="translation-content">
                              <div className="translation-title">{value as string}</div>
                              <div className="translation-button"><strong>🔘 {section.button?.[code] || '-'}</strong></div>
                            </div>
                          </span>
                        );
                      })}
                      {(!section.title || Object.keys(section.title).filter(k => k !== 'none').length === 0) && (
                        <span>Немає перекладів</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="section-actions">
                <Button 
                  appearance="ghost"
                  onClick={() => handleEditSection(section)}
                >
                  <FontAwesomeIcon icon={faEdit} style={{ marginRight: '8px' }} />
                  Редагувати
                </Button>
                <Button 
                  appearance="ghost"
                  color="red"
                  onClick={() => handleDeleteSection(section.id)}
                  disabled={isDefaultSection(section.id)}
                >
                  <FontAwesomeIcon icon={faTrash} style={{ marginRight: '8px' }} />
                  Видалити
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        open={isCreateModalOpen || isEditModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setIsEditModalOpen(false);
          setEditingSection(null);
        }}
        size="lg"
      >
        <Modal.Header>
          <Modal.Title>
            {isEditModalOpen ? 'Редагувати секцію' : 'Створити секцію'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form fluid>
            <Form.Group>
              <Form.ControlLabel>ID секції</Form.ControlLabel>
              <Input
                value={formData.id}
                disabled={true}
                style={{ backgroundColor: '#444', color: '#888' }}
              />
            </Form.Group>

            <Form.Group>
              <Form.ControlLabel>Дефолтна картка</Form.ControlLabel>
              <DefaultTranslationCard
                title={formData.title.none}
                button={formData.button.none}
                onUpdate={(title, button) => {
                  setFormData(prev => ({
                    ...prev,
                    title: { ...prev.title, none: title },
                    button: { ...prev.button, none: button }
                  }));
                }}
              />
            </Form.Group>

            <Form.Group>
              <Form.ControlLabel>Переклади</Form.ControlLabel>
              <div className="translation-cards-modal">
                {translationCards.slice(1).map((card) => (
                  <TranslationCard
                    key={card.countryCode}
                    card={card}
                    onUpdate={(updatedCard) => {
                      const updatedCards = translationCards.map(c => 
                        c.countryCode === card.countryCode ? updatedCard : c
                      );
                      setTranslationCards(updatedCards);
                    }}
                    onRemove={() => removeTranslation(card.countryCode)}
                  />
                ))}
              </div>
            </Form.Group>

            <Form.Group>
              <Form.ControlLabel>Додати переклад</Form.ControlLabel>
              <div className="add-translation-section">
                <SelectPicker
                  data={countryPickerData}
                  placeholder="Виберіть країну"
                  onSelect={handleCountrySelect}
                  style={{ width: '100%', marginBottom: '8px' }}
                />
                <Button 
                  appearance="ghost"
                  size="sm"
                  onClick={addTranslation}
                  disabled={translationCards.length >= 4}
                  style={{ width: '100%' }}
                >
                  <FontAwesomeIcon icon={faPlus} style={{ marginRight: '4px' }} />
                  Додати переклад
                </Button>
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            appearance="primary"
            onClick={handleSaveSection}
          >
            <FontAwesomeIcon icon={faSave} style={{ marginRight: '8px' }} />
            Зберегти
          </Button>
          <Button 
            appearance="ghost"
            onClick={() => {
              setIsCreateModalOpen(false);
              setIsEditModalOpen(false);
              setEditingSection(null);
            }}
          >
            <FontAwesomeIcon icon={faTimes} style={{ marginRight: '8px' }} />
            Скасувати
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SectionsManager; 